// Carrier errors are stored on the activity log as
// `params: { carrierCode, category, errorCode, retryState, rawMessage }`.
// `category` maps to a translatable merchant message; `errorCode` is the
// carrier's own code, kept for support and per-code aggregation; `rawMessage` is
// the untouched payload; `retryState` says whether an automatic replay is still
// scheduled.
type CarrierErrorParams = {
  carrierCode: string;
  category: string;
  retryState?: string;
  rawMessage: string;
};

function getCarrierErrorParams(params: unknown): CarrierErrorParams | null {
  if (
    params === null ||
    typeof params !== "object" ||
    !("category" in params) ||
    !("rawMessage" in params)
  ) {
    return null;
  }

  const record = params as Record<string, unknown>;
  if (
    typeof record.category !== "string" ||
    typeof record.rawMessage !== "string"
  ) {
    return null;
  }

  return {
    carrierCode:
      typeof record.carrierCode === "string" ? record.carrierCode : "",
    category: record.category,
    retryState:
      typeof record.retryState === "string" ? record.retryState : undefined,
    rawMessage: record.rawMessage,
  };
}

const CATEGORY_TRANSLATION_KEY: Record<string, string> = {
  "order-state": "errors.carrier.orderState",
  "network-unavailable": "errors.carrier.network",
  auth: "errors.carrier.auth",
  "carrier-config": "errors.carrier.carrierConfig",
  // `carrierInternalNoAction`, not `carrierInternal`: the older key says
  // "retrying will change nothing, contact support", which was written before
  // the automatic replay existed and is false about a third of the time. It was
  // unreachable while `retryState` shadowed the category — `carrier-internal`
  // only ever comes from carriers that classify their own errors, so the log
  // always carried a retry state — and became reachable again with this file's
  // fix. `trad-cli` cannot delete a key, and rewording a translated one leaves
  // the other 29 locales on the old text, hence a new key.
  "carrier-internal": "errors.carrier.carrierInternalNoAction",
  "address-recipient": "errors.carrier.addressRecipient",
  "parcel-data": "errors.carrier.parcelData",
};

// The retry state answers "will we try again?", never "what went wrong?".
// `retryState` is written on *every* label failure — `isFinal` is
// `!isTransient || attempts >= MAX_LABEL_ATTEMPTS`, so a permanent failure is
// `exhausted` on its very first attempt — so using it as the message swallowed
// the cause for every non-transient category, and swallowed the raw payload for
// the `unknown` ones. That is how a bpost `400` reached the merchant and the CSM
// as "the carrier could not produce the label after several automatic attempts"
// and nothing else. It is now a note appended to the cause, never a substitute.
//
// `noRetryLeft` states the scheduling fact and stops there. Its first wording
// added "our technical team has been notified", which is true — n8n and the
// blocked-returns report both fire on `isFinal` — but reads as "wait for us",
// and it lands on categories only the merchant can fix, on their first attempt:
// "Invalid delivery address or phone number. Our technical team has been
// notified." is precisely the sentence that sends someone to wait instead of
// correcting the address.
const RETRY_NOTE_TRANSLATION_KEY: Record<string, string> = {
  retrying: "errors.carrier.retryPending",
  exhausted: "errors.carrier.noRetryLeft",
};

// null for unknown/unmapped categories — the caller falls back to the raw message.
function getCategoryTranslationKey(category: string): string | null {
  return CATEGORY_TRANSLATION_KEY[category] ?? null;
}

type ResolvedCarrierError = {
  // Merchant-facing cause: the translated category, or the raw payload when the
  // category is unknown, or the legacy fallback for logs without params.
  message: string | null | undefined;
  // Whether an automatic replay is still scheduled, when the log says so.
  // Always secondary to `message`.
  retryNote: string | null;
  // The untouched carrier payload, kept for support. `null` when there is
  // nothing extra worth showing — legacy logs, or when `message` already is
  // the raw payload (unknown category).
  raw: string | null;
  // True when `message` is the carrier payload rather than a translated
  // sentence. Callers render it as code — it can be a 500-character XML blob
  // with no spaces to break on.
  isMessageRaw: boolean;
};

// Splits a carrier-error log into the cause to show the merchant, the retry note
// to show alongside it, and the raw payload to expose separately (e.g. behind a
// collapsible). `legacyFallback` covers old logs written before structured
// params existed.
export function resolveCarrierError(
  params: unknown,
  t: (key: string) => string,
  legacyFallback: () => string | null | undefined,
): ResolvedCarrierError {
  const carrierParams = getCarrierErrorParams(params);
  if (carrierParams) {
    const key = getCategoryTranslationKey(carrierParams.category);
    const retryKey = carrierParams.retryState
      ? RETRY_NOTE_TRANSLATION_KEY[carrierParams.retryState]
      : undefined;

    return {
      message: key ? t(key) : carrierParams.rawMessage,
      retryNote: retryKey ? t(retryKey) : null,
      raw: key ? carrierParams.rawMessage : null,
      isMessageRaw: !key,
    };
  }

  return {
    message: legacyFallback(),
    retryNote: null,
    raw: null,
    isMessageRaw: false,
  };
}

// Convenience wrapper for call sites that show a single line: the cause, with
// the retry note appended when there is one.
export function resolveCarrierErrorMessage(
  params: unknown,
  t: (key: string) => string,
  legacyFallback: () => string | null | undefined,
): string | null | undefined {
  const { message, retryNote } = resolveCarrierError(params, t, legacyFallback);
  if (!message) return message;

  return retryNote ? `${message} ${retryNote}` : message;
}

// The `generate-label` endpoint answers 422 with the same `category` and raw
// `message` the activity log stores, so the toast reads exactly like the log
// entry it announces. Anything else — a network error, another 4xx — is null
// and gets the generic error toast.
export function resolveLabelGenerationFailure(
  error: unknown,
  t: (key: string) => string,
): string | null {
  if (error === null || typeof error !== "object") return null;

  const body = error as Record<string, unknown>;
  if (
    body.code !== "LABEL_GENERATION_FAILED" ||
    typeof body.category !== "string" ||
    typeof body.message !== "string"
  ) {
    return null;
  }

  return (
    resolveCarrierError(
      { category: body.category, rawMessage: body.message },
      t,
      () => null,
    ).message ?? null
  );
}
